import { observer } from 'mobx-react';
import { CanvasWidget, DiagramEngine } from '@projectstorm/react-diagrams';
import { Drawer, FloatButton, Tooltip } from 'antd';
import { MenuFoldOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';

import { useStores } from '@/hooks/use-stores';

import AddDeviceMenu from '../AddDeviceMenu/AddDeviceMenu';
import PIDEditConnectionPicker from '../ConnectionPicker/PIDEditConnectionPicker';
import { PIDFullscreenEditorContainer } from './PIDFullscreenEditor.styles';
import DeviceDetailsCard from '../DeviceDetailsCard/DeviceDetailsCard';
import GridBackground from '../GridBackground';
import PIDModal from '../PIDModal';
import SensorActions from '../SensorActions';
import LoadingScreen from './LoadingScreen/LoadingScreen';
import Header from './Header/Header';
import ZoomButtons from './ZoomButtons/ZoomButtons';
import AddAssetSelectorDrawer from './AddAssetSelectorDrawer/AddAssetSelectorDrawer';

interface PIDFullscreenEditorProps {
  engine: DiagramEngine;
  diagramCoverOpacity: number;
  resetZoom: () => void;
  loadingScreenProgress: number;
}

const PIDFullscreenEditor = observer(
  ({ engine, diagramCoverOpacity, resetZoom, loadingScreenProgress }: PIDFullscreenEditorProps) => {
    const { pidEditStore } = useStores();
    const { t } = useTranslation();
    const diagramContainerRef = useRef<HTMLDivElement>(null);
    const [isAddAssetSelectorDrawerOpen, setIsAddAssetSelectorDrawerOpen] = useState(false);

    useEffect(() => {
      if (diagramCoverOpacity) {
        setTimeout(() => {
          resetZoom();
        }, 1);
      }
    }, [diagramCoverOpacity]);

    const handleAddAssetOpenDrawer = () => setIsAddAssetSelectorDrawerOpen(true);

    return (
      <Drawer open={pidEditStore.isInEditMode} width="100vw" closable={false} motion={{ visible: false }} push={false}>
        <PIDFullscreenEditorContainer>
          <Header diagramCoverOpacity={diagramCoverOpacity} />
          {!diagramCoverOpacity || pidEditStore.isLoadingUnusedSerialNumbers ? (
            <LoadingScreen progressPercent={loadingScreenProgress} />
          ) : (
            <>
              <GridBackground isVisible={pidEditStore.isInEditMode} />
              <div className="diagram-cover" style={{ opacity: diagramCoverOpacity }} ref={diagramContainerRef}>
                <ZoomButtons engine={engine} resetZoom={resetZoom} />
                <CanvasWidget className="diagram-container" engine={engine} />
              </div>
              <AddDeviceMenu />
              <PIDEditConnectionPicker />
              <DeviceDetailsCard />
              <SensorActions />
              <PIDModal />
              {!isAddAssetSelectorDrawerOpen && (
                <Tooltip title={t('openDrawerTooltip')} placement="left" color="blue">
                  <FloatButton
                    icon={<MenuFoldOutlined />}
                    shape="square"
                    className="open-drawer-button"
                    onClick={handleAddAssetOpenDrawer}
                  />
                </Tooltip>
              )}
              <AddAssetSelectorDrawer
                isAddAssetSelectorDrawerOpen={isAddAssetSelectorDrawerOpen}
                setIsAddAssetSelectorDrawerOpen={setIsAddAssetSelectorDrawerOpen}
                diagramContainerRef={diagramContainerRef}
              />
            </>
          )}
        </PIDFullscreenEditorContainer>
      </Drawer>
    );
  },
);

export default PIDFullscreenEditor;
